import { Check, Clear, CloudUpload, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import VirtualHubIndicator from "components/VirtualHubIndicator";
import { toQueryString } from "utils";
import { get, post, patch, del } from "utils/io";

export default function HubMonitoringList({ programId }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [allMonitoringLists, setAllMonitoringLists] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [orgId, setOrgId] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [csvFile, setCsvFile] = useState(null);
  const [monitoringListName, setMonitoringListName] = useState("");
  const [reportRecipients, setReportRecipients] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState(null);
  const [isAddingToExisting, setIsAddingToExisting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);

  const [hubs, setHubs] = useState([]);

  const dataGridColumns = [
    {
      field: "hub_id",
      flex: 1,
      headerName: "Hub ID",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>{params.row.hubId}</Typography>
          {params.row.isVirtual && (
            <Box sx={{ ml: "10px" }}>
              <VirtualHubIndicator />
            </Box>
          )}
        </Box>
      ),
    },
    {
      field: "group",
      flex: 1,
      headerName: "Group",
      renderCell: (params) => (
        <Typography sx={{ fontSize: "14px" }}>
          {params.row.hubGroup?.name || "N/A"}
        </Typography>
      ),
    },
    {
      field: "connected",
      align: "center",
      flex: 0.5,
      headerAlign: "center",
      headerName: "Connected",
      renderCell: (params) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="100%"
          height="100%"
        >
          {params.row.connected ? (
            <Check sx={{ color: theme.palette.success.dark }} />
          ) : (
            <Clear color="error" />
          )}
        </Box>
      ),
      width: 150,
    },
  ];

  const fetchProgramDetails = useCallback(async () => {
    try {
      const { data } = await get(`/programs/${programId}`);
      setOrgId(data.org);
      return data.org;
    } catch (error) {
      setErrorMessage("Failed to fetch program details.");
      return null;
    }
  }, [programId]);

  const fetchAllMonitoringLists = useCallback(async (organizationId) => {
    if (!organizationId) return;

    try {
      const { data } = await get(
        `/hub_monitoring/lists?org_id=${organizationId}`
      );
      if (data.results && data.results.length > 0) {
        const sorted = data.results.sort((a, b) => {
          if (a.active && !b.active) return -1;
          if (!a.active && b.active) return 1;
          return (
            new Date(b.uploaded_at).getTime() -
            new Date(a.uploaded_at).getTime()
          );
        });
        setAllMonitoringLists(sorted);
      } else {
        setAllMonitoringLists([]);
      }
    } catch (error) {
      setErrorMessage("Failed to fetch monitoring lists.");
    }
  }, []);

  const fetchHubs = useCallback(
    async (sortField) => {
      setLoading(true);

      const payload = {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        ...(searchQuery && { hub_id: searchQuery }),
        ...(sortField && { ordering: sortField }),
      };

      const queryString = toQueryString(payload);

      try {
        const {
          data: { hubs: fetchedHubs, total_count: totalCount },
        } = await get(`/hubs?${queryString}`);

        const mappedHubs = fetchedHubs.map(
          ({
            connected,
            hub_group: hubGroup,
            is_virtual: isVirtual,
            hub_id: hubId,
          }) => ({
            id: hubId,
            hubId,
            connected,
            hubGroup,
            isVirtual,
          })
        );
        setHubs(mappedHubs);
        setTotalCount(totalCount);
      } catch (error) {
        setErrorMessage("Failed to fetch hubs. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [paginationModel, searchQuery, setTotalCount]
  );

  const updateHubGrid = useCallback(() => {
    const debouncedUpdate = debounce(async () => {
      setLoading(true);
      const sortField = sortModel[0]?.field;
      const sortDirection = sortModel[0]?.sort === "desc" ? "-" : "";
      const sortParam = sortField ? `${sortDirection}${sortField}` : undefined;

      await fetchHubs(sortParam);
      setLoading(false);
    }, 300);

    debouncedUpdate();
    return debouncedUpdate.cancel;
  }, [fetchHubs, sortModel]);

  useEffect(() => {
    updateHubGrid();
  }, [updateHubGrid]);

  useEffect(() => {
    const initializeData = async () => {
      const organizationId = await fetchProgramDetails();
      if (organizationId) {
        await fetchAllMonitoringLists(organizationId);
      }
    };
    initializeData();
  }, [fetchProgramDetails, fetchAllMonitoringLists]);

  const handleSearch = (e) => {
    e.preventDefault();
    updateHubGrid();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      setErrorMessage("");
    }
  };

  const handleStartMonitoring = async () => {
    if (selectedHubs.length === 0) {
      setErrorMessage("Please select at least one hub to monitor.");
      return;
    }

    if (!monitoringListName.trim()) {
      setErrorMessage("Please enter a monitoring list name.");
      return;
    }

    if (!orgId) {
      setErrorMessage(
        "Organization ID not available. Please refresh the page."
      );
      return;
    }

    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const csvContent = `hub_id\n${selectedHubs.join("\n")}`;
      const csvBlob = new Blob([csvContent], { type: "text/csv" });

      const formData = new FormData();
      formData.append("file", csvBlob, "monitoring_hubs.csv");
      formData.append("org_id", orgId);
      formData.append("name", monitoringListName.trim());
      formData.append("report_recipients", reportRecipients.trim());

      await post("/hub_monitoring/upload_list", formData);

      setSuccessMessage(
        `Successfully started monitoring ${selectedHubs.length} hub(s). Reports will be sent weekly every Monday.`
      );
      setSelectedHubs([]);
      setMonitoringListName("");
      setReportRecipients("");

      await fetchAllMonitoringLists(orgId);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to start monitoring. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadCsv = async () => {
    if (!csvFile) {
      setErrorMessage("Please select a CSV file to upload.");
      return;
    }

    if (!monitoringListName.trim()) {
      setErrorMessage("Please enter a monitoring list name.");
      return;
    }

    if (!orgId) {
      setErrorMessage(
        "Organization ID not available. Please refresh the page."
      );
      return;
    }

    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      formData.append("org_id", orgId);
      formData.append("name", monitoringListName.trim());
      formData.append("report_recipients", reportRecipients.trim());

      await post("/hub_monitoring/upload_list", formData);

      setSuccessMessage(
        "Successfully uploaded monitoring list. Reports will be sent weekly every Monday."
      );
      setCsvFile(null);
      setMonitoringListName("");
      setReportRecipients("");

      await fetchAllMonitoringLists(orgId);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to upload monitoring list. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedHubs(newSelection);
  };

  const handleGenerateReport = async (listId) => {
    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await post("/hub_monitoring/generate_report", {
        list_id: listId,
        days: 7,
      });

      setSuccessMessage(
        "Report generated successfully. Check your email for the report."
      );
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to generate report. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = (list) => {
    setListToDelete(list);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setListToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!listToDelete) return;

    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await del(`/hub_monitoring/lists/${listToDelete.id}`);

      setSuccessMessage(
        `Successfully deleted monitoring list "${listToDelete.name}".`
      );
      handleCloseDeleteDialog();
      await fetchAllMonitoringLists(orgId);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to delete monitoring list. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (list) => {
    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await patch(`/hub_monitoring/lists/${list.id}`, { active: !list.active });

      setSuccessMessage(
        list.active
          ? `Monitoring list "${list.name}" has been deactivated.`
          : `Monitoring list "${list.name}" is now active.`
      );
      await fetchAllMonitoringLists(orgId);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to update monitoring list. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAddToExistingDialog = async () => {
    if (!orgId) {
      setErrorMessage(
        "Organization ID not available. Please refresh the page."
      );
      return;
    }

    try {
      const { data } = await get(`/hub_monitoring/lists?org_id=${orgId}`);
      setAllMonitoringLists(data.results || []);
      setDialogOpen(true);
    } catch (error) {
      setErrorMessage("Failed to fetch monitoring lists. Please try again.");
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedListId(null);
  };

  const handleSelectList = (listId) => {
    setSelectedListId(listId);
  };

  const handleAddToExistingList = async () => {
    if (!selectedListId) {
      setErrorMessage("Please select a monitoring list.");
      return;
    }

    if (isAddingToExisting && selectedHubs.length === 0) {
      setErrorMessage("Please select at least one hub to add.");
      return;
    }

    if (!isAddingToExisting && !csvFile) {
      setErrorMessage("Please select a CSV file to upload.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      let csvBlob;
      if (isAddingToExisting) {
        const csvContent = `hub_id\n${selectedHubs.join("\n")}`;
        csvBlob = new Blob([csvContent], { type: "text/csv" });
      } else {
        csvBlob = csvFile;
      }

      const formData = new FormData();
      formData.append("file", csvBlob, "additional_hubs.csv");

      await post(`/hub_monitoring/lists/${selectedListId}/add_hubs`, formData);

      setSuccessMessage(`Successfully added hubs to the monitoring list.`);

      if (isAddingToExisting) {
        setSelectedHubs([]);
      } else {
        setCsvFile(null);
      }

      handleCloseDialog();
      await fetchAllMonitoringLists(orgId);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to add hubs to monitoring list. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={3}>
      {allMonitoringLists.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Monitoring Lists
          </Typography>
          <Stack spacing={2}>
            {allMonitoringLists.map((list) => (
              <Card key={list.id}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6">{list.name}</Typography>
                        <Chip
                          label={list.active ? "Active" : "Inactive"}
                          color={list.active ? "success" : "default"}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Total Hubs:</strong> {list.hub_count} hub(s)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Report Recipients:</strong>{" "}
                        {list.report_recipients?.join(", ") || "None"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Uploaded:</strong>{" "}
                        {new Date(list.uploaded_at).toLocaleDateString()} by{" "}
                        {list.uploaded_by || "Unknown"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleGenerateReport(list.id)}
                        disabled={submitting}
                        startIcon={submitting && <CircularProgress size={16} />}
                      >
                        GENERATE REPORT
                      </Button>
                      {!list.active && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleToggleActive(list)}
                          disabled={submitting}
                        >
                          SET AS ACTIVE
                        </Button>
                      )}
                      {list.active && (
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          onClick={() => handleToggleActive(list)}
                          disabled={submitting}
                        >
                          DEACTIVATE
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(list)}
                        disabled={submitting}
                        startIcon={<Delete />}
                      >
                        DELETE
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Divider sx={{ my: 3 }} />
        </Box>
      )}

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}

      <Typography variant="h6">Create Monitoring List</Typography>

      <Tabs value={currentTab} onChange={handleTabChange}>
        <Tab label="SELECT FROM HUB LIST" />
        <Tab label="UPLOAD CSV FILE" />
      </Tabs>

      <Stack spacing={2}>
        <TextField
          label="Monitoring List Name"
          placeholder="A descriptive name for this monitoring list"
          variant="outlined"
          fullWidth
          required
          value={monitoringListName}
          onChange={(e) => setMonitoringListName(e.target.value)}
          helperText="A descriptive name for this monitoring list"
        />
        <TextField
          label="Report Recipients (Email)"
          placeholder="Comma-separated email addresses that will receive the weekly reports"
          variant="outlined"
          fullWidth
          value={reportRecipients}
          onChange={(e) => setReportRecipients(e.target.value)}
          helperText="Comma-separated email addresses that will receive the weekly reports"
        />
      </Stack>

      {currentTab === 0 && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Select the hubs you want to monitor from the list below. Once you
            click &quot;Start Monitoring&quot;, weekly reports will be generated
            every Monday, identifying hubs with no data transmissions in the
            previous week.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <TextField
              label="Search by Hub ID"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <Button type="submit" variant="contained">
              SEARCH
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchQuery("");
                updateHubGrid();
              }}
            >
              CLEAR
            </Button>
          </Box>

          {/* Hub List with Checkboxes */}
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              loading={loading}
              columns={dataGridColumns}
              rows={hubs}
              checkboxSelection
              onRowSelectionModelChange={handleSelectionChange}
              rowSelectionModel={selectedHubs}
              keepNonExistentRowsSelected
              sortingMode="server"
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[20, 25, 50, 100]}
              rowCount={totalCount}
              onSortModelChange={(model) => setSortModel([...model])}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                },
              }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            {selectedHubs.length} row{selectedHubs.length !== 1 ? "s" : ""}{" "}
            selected
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => {
                setIsAddingToExisting(true);
                handleOpenAddToExistingDialog();
              }}
              disabled={selectedHubs.length === 0 || submitting}
            >
              ADD TO EXISTING LIST
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleStartMonitoring}
              disabled={selectedHubs.length === 0 || submitting}
              startIcon={submitting && <CircularProgress size={20} />}
            >
              {submitting ? "Starting Monitoring..." : "START MONITORING"}
            </Button>
          </Box>
        </Stack>
      )}

      {currentTab === 1 && (
        <Stack spacing={3} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV file with hub IDs (one per line, with a header row
            &quot;hub_id&quot;). Weekly reports will be generated every Monday,
            identifying hubs with no data transmissions in the previous week.
          </Typography>

          <Box
            sx={{
              border: "2px dashed",
              borderColor: "grey.400",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              width: "100%",
              maxWidth: 600,
            }}
          >
            <CloudUpload sx={{ fontSize: 48, color: "grey.500", mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              {csvFile ? csvFile.name : "No file selected"}
            </Typography>
            <Button variant="outlined" component="label" sx={{ mt: 2 }}>
              CHOOSE CSV FILE
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleCsvFileChange}
              />
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => {
                setIsAddingToExisting(false);
                handleOpenAddToExistingDialog();
              }}
              disabled={!csvFile || submitting}
            >
              ADD TO EXISTING LIST
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleUploadCsv}
              disabled={!csvFile || submitting}
              startIcon={submitting && <CircularProgress size={20} />}
            >
              {submitting ? "Uploading..." : "UPLOAD MONITORING LIST"}
            </Button>
          </Box>
        </Stack>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Monitoring List</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose an existing monitoring list to add the selected hubs to:
          </Typography>
          <List>
            {allMonitoringLists.map((list) => (
              <ListItem key={list.id} disablePadding>
                <ListItemButton
                  selected={selectedListId === list.id}
                  onClick={() => handleSelectList(list.id)}
                  sx={{
                    border: "1px solid",
                    borderColor:
                      selectedListId === list.id ? "primary.main" : "grey.300",
                    borderRadius: 1,
                    mb: 1,
                    "&.Mui-selected": {
                      backgroundColor: "primary.50",
                      "&:hover": {
                        backgroundColor: "primary.100",
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={list.name}
                    secondary={`${list.hub_count} hub(s) • ${
                      list.active ? "Active" : "Inactive"
                    }`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          {allMonitoringLists.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 3 }}
            >
              No monitoring lists found for this organization.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleAddToExistingList}
            variant="contained"
            color="primary"
            disabled={!selectedListId || submitting}
            startIcon={submitting && <CircularProgress size={20} />}
          >
            {submitting ? "Adding..." : "Add Hubs"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the monitoring list &quot;
            {listToDelete?.name}&quot;?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. The list contains{" "}
            {listToDelete?.hub_count} hub(s).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            variant="outlined"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={submitting}
            startIcon={submitting && <CircularProgress size={20} />}
          >
            {submitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

HubMonitoringList.propTypes = {
  programId: PropTypes.string.isRequired,
};
